import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  Email,
  Phone,
  WhatsApp,
  Notifications,
  ContactPhone,
  Visibility,
  VisibilityOff,
  Refresh,
  Security,
  SmartToy,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

import { Contact, ContactTagSummary } from '../../types';
import { formatarPhone, formatarPhoneOnInput, getPhoneRuleByDdi } from '../../utils/formatToView';
import { clientService } from '../../services/clientService';
import DDISelect from '../selects/DDISelect';
import TagsMultiSelect from '../selects/TagsMultiSelect';

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Contact>) => Promise<void>;
  editingContact: Contact | null;
  clientId?: number;
  loading: boolean;
}

// Validação customizada
const validateContact = (data: Partial<Contact> & { ddi?: string }, isEditing: boolean = false) => {
  const errors: Record<string, string> = {};
  const phoneRule = getPhoneRuleByDdi(data.ddi || '55');

  if (!data.nome || data.nome.trim() === '') {
    errors.nome = 'Nome é obrigatório';
  }

  if (!data.tipo) {
    errors.tipo = 'Tipo é obrigatório';
  }

  if (data.tipo === 'email') {
    if (!data.email || data.email.trim() === '') {
      errors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Email inválido';
    }
  }

  if (data.tipo === 'fixo') {
    if (!data.numero || data.numero.trim() === '') {
      errors.numero = 'Número é obrigatório';
    } else if (data.numero.replace(/\D/g, '').length < 10) {
      errors.numero = 'Número deve ter pelo menos 10 dígitos';
    }
  }

  if (data.tipo === 'celular') {
    if (!data.numero || data.numero.trim() === '') {
      errors.numero = 'Número é obrigatório';
    } else {
      const numberLength = data.numero.replace(/\D/g, '').length;

      if (numberLength < phoneRule.minDigits || numberLength > phoneRule.maxDigits) {
        errors.numero = phoneRule.minDigits === phoneRule.maxDigits
          ? `Número deve ter ${phoneRule.maxDigits} dígitos`
          : `Número deve ter entre ${phoneRule.minDigits} e ${phoneRule.maxDigits} dígitos`;
      }
    }
  }

  return errors;
};

const parseContactTags = (tags: unknown): number[] => {
  if (!tags) return [];

  let rawArray: any[] = [];
  if (Array.isArray(tags)) {
    rawArray = tags;
  } else if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) rawArray = parsed;
    } catch (e) {
      rawArray = [];
    }
  }

  return rawArray
    .map((tag) => {
      if (typeof tag === 'object' && tag !== null && 'id' in tag) {
        return Number(tag.id);
      }
      if (typeof tag === 'number') return tag;
      if (typeof tag === 'string' && !isNaN(Number(tag))) return Number(tag);
      return null;
    })
    .filter((v): v is number => v !== null && !isNaN(v));
};

const ContactForm: React.FC<ContactFormProps> = ({
  open,
  onClose,
  onSubmit,
  editingContact,
  clientId,
  loading,
}) => {
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Partial<Omit<Contact, 'tags'>> & { ddi?: string; tags?: any }>({
    defaultValues: {
      cliente_id: clientId,
      nome: '',
      tipo: 'email',
      email: '',
      numero: '',
      ddi: '55',
      is_whatsapp: false,
      is_default: false,
      recebe_notificacao: true,
      bot_desativado: false,
      tags: [] as any[],
    },
  });

  const tipoWatch = watch('tipo');
  const isWhatsappWatch = watch('is_whatsapp');

  // Reset form when dialog opens/closes or editing contact changes
  React.useEffect(() => {
    if (open) {
      if (editingContact) {
        let editingNumero = editingContact.numero || '';
        let ddi = editingContact.ddi || '55';

        if (editingContact.tipo === 'celular') {
          if (editingNumero.startsWith(ddi)) {
            editingNumero = editingNumero.substring(ddi.length);
          }
        }

        const editingData = {
          ...editingContact,
          password: '',
          ddi,
          nome: editingContact.nome ?? '',
          email: editingContact.email ?? '',
          numero: editingNumero ? formatarPhone(editingNumero, ddi) : '',
          bot_desativado: editingContact.bot_desativado || false,
          tags: parseContactTags(editingContact.tags) as any,
        };
        reset(editingData);
      } else {
        reset({
          cliente_id: clientId,
          nome: '',
          tipo: 'email',
          email: '',
          numero: '',
          ddi: '55',
          is_whatsapp: false,
          is_default: false,
          recebe_notificacao: true,
          bot_desativado: false,
          tags: [],
        });
      }
      setFormErrors({});
    }
  }, [open, editingContact, clientId, reset]);

  const ddiWatch = watch('ddi');

  const handleFormSubmit = async (data: Partial<Contact> & { ddi?: string }) => {
    setFormErrors({});

    // Validação customizada
    const validationErrors = validateContact(data, !!editingContact);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    const contactData: Partial<Contact> & { ddi?: string } = {
      ...data,
      cliente_id: clientId,
      is_whatsapp: data.is_whatsapp || false,
      is_default: data.is_default || false,
      recebe_notificacao: data.recebe_notificacao !== false,
      bot_desativado: data.is_whatsapp ? (data.bot_desativado || false) : false,
      tags: data.tags?.length ? data.tags : null,
      numero: data.numero?.replace(/\D/g, ''),
    };

    if (data.tipo === 'celular') {
      contactData.ddi = data.ddi || '55';
    } else {
      delete contactData.ddi;
    }

    try {
      await onSubmit(contactData);
    } catch (error: any) {
      // Capturar erros do backend
      console.error('Erro ao salvar contato:', error);

      if (error.response?.data?.errors) {
        // Erros de validação do express-validator
        const backendErrors: Record<string, string> = {};

        error.response.data.errors.forEach((err: any) => {
          if (err.path) {
            backendErrors[err.path] = err.msg;
          }
        });

        setFormErrors(backendErrors);
      } else if (error.response?.data?.message) {
        // Erro geral do backend
        setFormErrors({ general: error.response.data.message });
      } else {
        // Erro genérico
        setFormErrors({ general: 'Erro ao salvar contato. Tente novamente.' });
      }
    }
  };

  const handleClose = () => {
    setFormErrors({});
    setShowPassword(false);
    reset();
    onClose();
  };

  const handleUseClientName = async () => {
    if (!clientId) return;
    try {
      const client = await clientService.getById(clientId);
      if (client && client.nome) {
        setValue('nome', client.nome, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
    } catch (error) {
      console.error('Erro ao buscar nome do cliente:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {editingContact ? 'Editar Contato' : 'Novo Contato'}
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {/* Exibir erro geral do backend */}
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Nome do Contato */}
            <Grid item xs={12}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <Controller
                  name="nome"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      required
                      label="Nome do Contato"
                      placeholder="Ex: João Silva"
                      error={!!formErrors.nome}
                      helperText={formErrors.nome}
                      InputLabelProps={{ shrink: !!field.value ? true : undefined }}
                    />
                  )}
                />
                {clientId && (
                  <Button
                    variant="outlined"
                    onClick={handleUseClientName}
                    sx={{ minWidth: 'max-content', height: '40px' }}
                  >
                    Utilizar nome do cliente
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Tipo de Contato e Campo de Contato na mesma linha */}
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: 'flex-start'
              }}>
                {/* Tipo de Contato */}
                <Box sx={{ minWidth: { xs: '100%', sm: 150 }, flexShrink: 0 }}>
                  <Controller
                    name="tipo"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!formErrors.tipo}>
                        <InputLabel>Tipo de Contato</InputLabel>
                        <Select
                          {...field}
                          label="Tipo de Contato"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            setValue('email', '');
                            setValue('numero', '');
                            setValue('is_whatsapp', false);
                            setValue('bot_desativado', false);
                            setFormErrors((prev) => {
                              const { email, numero, ...rest } = prev;
                              return rest;
                            });
                          }}
                          size="small"
                        >
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="fixo">Fixo</MenuItem>
                          <MenuItem value="celular">Celular</MenuItem>
                        </Select>
                        {formErrors.tipo && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {formErrors.tipo}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>

                {/* Campo de Contato */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  {/* Campo Email */}
                  {tipoWatch === 'email' && (
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          fullWidth
                          label="Email"
                          type="email"
                          placeholder="exemplo@email.com"
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          InputProps={{
                            startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                          }}
                        />
                      )}
                    />
                  )}

                  {/* Campo Fixo */}
                  {tipoWatch === 'fixo' && (
                    <Controller
                      name="numero"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          fullWidth
                          label="Número"
                          placeholder="(11) 3333-3333"
                          error={!!formErrors.numero}
                          helperText={formErrors.numero}
                          onChange={(e) => {
                            const formatted = formatarPhoneOnInput(e.target.value, 10);
                            field.onChange(formatted);
                          }}
                          InputProps={{
                            startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                          }}
                        />
                      )}
                    />
                  )}

                  {/* Campo Celular */}
                  {tipoWatch === 'celular' && (
                    <Box display="flex" gap={1}>
                      <Box sx={{ width: 200 }}>
                        <Controller
                          name="ddi"
                          control={control}
                          render={({ field }) => (
                            <DDISelect
                              value={field.value || '55'}
                              onChange={(newDdi) => {
                                field.onChange(newDdi);

                                const currentNumber = getValues('numero') || '';
                                if (currentNumber) {
                                  setValue('numero', formatarPhoneOnInput(currentNumber, newDdi), {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Controller
                        name="numero"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            size="small"
                            {...field}
                            fullWidth
                            label="Número"
                            placeholder="(11) 99999-9999"
                            error={!!formErrors.numero}
                            helperText={formErrors.numero}
                            onChange={(e) => {
                              const formatted = formatarPhoneOnInput(e.target.value, ddiWatch || '55');
                              field.onChange(formatted);
                            }}
                            InputProps={{
                              startAdornment: <ContactPhone sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>



            {/* Configurações */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Configurações
              </Typography>
            </Grid>

            {/* WhatsApp (apenas para celular) */}
            {tipoWatch === 'celular' && (
              <Grid item xs={12}>
                <Controller
                  name="is_whatsapp"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          color="success"
                          onChange={(e) => {
                            const checked = e.target.checked;
                            field.onChange(checked);
                            if (!checked) {
                              setValue('bot_desativado', false);
                            }
                          }}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <WhatsApp color="success" />
                          <Typography>Este número possui WhatsApp</Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>
            )}

            {/* Desativar Bot (apenas para WhatsApp) */}
            {isWhatsappWatch && (
              <Grid item xs={12}>
                <Controller
                  name="bot_desativado"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="error"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <SmartToy color="error" />
                          <Typography>Desativar bot para este contato</Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>
            )}

            {/* Contato Padrão */}
            <Grid item xs={12}>
              <Controller
                name="is_default"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        color="warning"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <ContactPhone color="warning" />
                        <Typography>Contato padrão do cliente</Typography>
                      </Box>
                    }
                  />
                )}
              />
            </Grid>

            {/* Receber Notificações */}
            <Grid item xs={12}>
              <Controller
                name="recebe_notificacao"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Notifications color="primary" />
                        <Typography>Receber notificações neste contato</Typography>
                      </Box>
                    }
                  />
                )}
              />
            </Grid>            

            {/* Tags */}
            <Grid item xs={12}>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <TagsMultiSelect
                    value={field.value || []}
                    onChange={field.onChange}
                    label="Tags"
                    placeholder="Selecione as tags"
                  />
                )}
              />
            </Grid>

            {/* Informações Adicionais */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Dica:</strong> Contatos marcados para receber notificações serão usados
                  para envio de lembretes de vencimento, confirmações de pagamento e outras
                  comunicações importantes.
                  <br /><strong>Contato Padrão:</strong> O contato padrão será usado prioritariamente
                  para envio de mensagens de aniversário e outras comunicações especiais.
                  Apenas um contato de cada tipo (email, fixo ou celular) por cliente pode ser
                  marcado como padrão. O primeiro contato de cada tipo é marcado automaticamente.

                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ContactPhone />}
          >
            {loading ? 'Salvando...' : editingContact ? 'Atualizar' : 'Criar Contato'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactForm;